// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const projectAssetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  item: z.object({ id: z.string(), name: z.string() }),
  history: z.array(
    z.object({
      id: z.string(),
      start: dateTimeSchema,
      end: dateTimeSchema.nullable(),
    })
  ),
});

const projectAssetsResponseSchema = z
  .object({
    // Unfortunately the API does not support returning an empty array so this
    // schema transforms null to an empty array.
    data: z.array(projectAssetSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, assets: data ?? [] }));

type ProjectAssetsResponse = z.infer<typeof projectAssetsResponseSchema>;

/**
 * Requests the assets for a project from the remote API.
 *
 * @param key - The key of the project.
 */
export async function getProjectAssets(
  key: string
): Promise<ProjectAssetsResponse> {
  const url = `/v1/project/${key}/asset`;
  const response = await api.get(url);

  return parseResponse(projectAssetsResponseSchema, response.data, {
    schemaName: 'ProjectAssets',
    endpoint: url,
  });
}
